// src/features/admin-options/components/RoommatikSettingsTab.tsx
//
// «Opciones de Roommatik» (mockup 13, third tab) — Admin/SuperAdmin only:
// `nameRetentionDays`, `viewerCanExport`.
import React, { useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, Checkbox, FormControlLabel, MenuItem, TextField, Typography } from '@mui/material';
import { SettingsApplicationsRounded as RoommatikSettingsIcon } from '@mui/icons-material';
import { useDataProvider, useNotify, useTranslate } from 'react-admin';
import { useHotelScope } from '@/hooks/useHotelScope';
import type { GroupSettings } from '@/types/analytics';

export const RoommatikSettingsTab: React.FC = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const scope = useHotelScope();

  const [groupId, setGroupId] = useState<string>('');
  const [settings, setSettings] = useState<GroupSettings | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!groupId && scope.groups.length > 0) setGroupId(scope.groups[0].id);
  }, [scope.groups, groupId]);

  useEffect(() => {
    if (!groupId) return;
    (dataProvider as any).getGroupSettings(groupId).then((res: { data: GroupSettings }) => setSettings(res.data));
  }, [dataProvider, groupId]);

  const handleSave = async () => {
    if (!groupId || !settings) return;
    setSaving(true);
    try {
      await (dataProvider as any).updateGroupSettings(groupId, settings);
      notify(translate('options.toast.saved'), { type: 'success' });
    } catch {
      notify(translate('options.toast.saveError'), { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return null;

  return (
    <Box sx={{ maxWidth: 480 }}>
      {/* Header — icon + heading + one-line description, shared across every admin-options tab */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <RoommatikSettingsIcon color="primary" />
        <Typography variant="h6">{translate('options.roommatik.help.title')}</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {translate('options.roommatik.help.description')}
      </Typography>

      <Card variant="outlined">
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            select
            size="small"
            label={translate('options.roommatik.selectGroup')}
            value={groupId}
            onChange={(e) => setGroupId(e.target.value)}
          >
            {scope.groups.map((g) => (
              <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
            ))}
          </TextField>

          <TextField
            type="number"
            size="small"
            label={translate('options.roommatik.fields.nameRetentionDays')}
            value={settings.nameRetentionDays}
            onChange={(e) => setSettings({ ...settings, nameRetentionDays: Number(e.target.value) })}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={settings.viewerCanExport}
                onChange={(e) => setSettings({ ...settings, viewerCanExport: e.target.checked })}
              />
            }
            label={translate('options.roommatik.fields.viewerCanExport')}
          />

          <Box>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {translate('common.actions.save')}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default RoommatikSettingsTab;
